import React, { useState, useRef, useEffect } from 'react'
import PropTypes, { string } from 'prop-types'
import Spinner from '../spinner'
import FullSearchBar from '../search-bars/full-search-bar'
import moment from 'moment'
import SingleBlogPost from './single-blog-post'
import { getUrlForBlogPost } from '../../lib/blog-utils/blog-utils'
import BlogLoader from './blog-loader'
import { AppUser } from '../../lib/blog-utils/blog-post-widget-types'
import { MenuBar } from './menu-bar/menu'
import { BlogPost } from '../../models/BlogPost'
import { useEffectAsync } from '../../hooks/async-hooks'
import { useParameters, useHistory, useAppState, useLocation, useTranslation } from '../../utils'
import { getGetsimilarposts, getPostsId, getPostsIdLang, getPostsbyslugSlug } from '../../services/api'
import { useInfiniteScroll } from '../../hooks/display-hooks'
import { changeLanguage, simpleLang, blogUrl } from './utils'
import MetaTags from 'react-meta-tags'

const AMOUNT_OF_SIMILAR_POSTS = 20

function SingleBlogPage(p: React.ComponentProps<typeof SingleBlogPost> & {
  orignalLang: string
}) {
  let history = useHistory();
  const location = useLocation();
  const params = useParameters<{
    blogSlug: string,
    lang: string,
  }>();
  const observer = useRef<IntersectionObserver>();

  useEffect(() => {
    observer.current = new IntersectionObserver((e) => {
      if (e[0].isIntersecting && location.pathname !== blogUrl(params.lang, p.post)) {
        // console.log("Intersecting", {lang: params.lang, post: p.post.slug});
        history.replace({
          pathname: blogUrl(params.lang, p.post),
          state: {
            noResetScroll: true
          }
        })
      }
    }, { threshold: 0.05 });
    return () => observer.current?.disconnect();
  }, [params.lang, p.post]);

  return <div ref={(node => node && observer.current?.observe(node))}>
    <SingleBlogPost {...p} />
  </div>
}
function changePostLanguage(blog: BlogPost, language: string) {
  return changeLanguage({
    blog,
    language,
    get: id => getPostsId(id, { lang: language }),
    getByLang: id => getPostsIdLang(id, language),
  })
}
export default BlogPage;
export function BlogPage() {
  const params = useParameters<{
    blogSlug: string,
    lang: string,
  }>();
  let history = useHistory();
  const { t } = useTranslation();
  const [similarPosts, setSimilarPosts] = useState<number[]>([]);
  const { data: posts, loading, lastElementRef, setData: setPosts, loadMoreData } = useInfiniteScroll(async ({ skip }) => {
    const [slug, slugLang] = params.blogSlug.split('--');
    if (skip == 0) {
      let post = (await getPostsbyslugSlug(slug, {
        lang: slugLang ?? params.lang
      }))?.[0];
      if (slugLang && slugLang != params.lang) {
        post = await changePostLanguage(post, params.lang);
      }

      if (!post) {
        return [];
      }
      const postIdsToLoad = await getGetsimilarposts({ postId: post.id, limit: AMOUNT_OF_SIMILAR_POSTS })
      setSimilarPosts(postIdsToLoad);
      return [{
        originalLang: slugLang ?? params.lang,
        ...post,
      }]
    } else {
      const id = similarPosts[skip - 1];
      if (!id) return []
      const post = await getPostsId(id, {})
      return [await changePostLanguage(post, params.lang)]
    }
  }, {
    perPage: 1,
    threshold: 0.5
  });

  useEffect(() => {
    posts.forEach(async (p, index) => {
      const path = window.location.pathname;
      const translated = await changePostLanguage(p, params.lang);
      if (path.indexOf(p.slug!) != -1) {
        history.replace({
          pathname: blogUrl(params.lang, translated),
          state: {
            noResetScroll: true
          }
        });
      }
      setPosts(p => {
        let newPosts = [...p];
        newPosts[index] = translated;
        return newPosts;
      });
    });
  }, [params.lang]);
  const appState = useAppState();
  return (<div className="blogWrapper">
    {(posts.length > 0 ?
      posts.map((post, index) => (
        <div key={post.id}>
          <MetaTags>
            <title>{t('seo_title_home')}</title>
          </MetaTags>
          <SingleBlogPage
            post={post}
            orignalLang={post.originalLang!}
            key={post.id}
            bottomRef={index === posts.length - 1 ? lastElementRef : undefined}
            isLoggedIn={!!appState.user}
          />
        </div>
      ))
      : !loading && <div><p>{t('faulty-search')}.</p></div>)}
    {loading && <BlogLoader />}
  </div>)
}